import { clamp, dist } from '../../core/math';
import type { Actor } from '../../sim/actor';
import { registerBossScript } from '../../sim/ai/brains';
import type { MonsterDef } from '../../sim/registry';
import { shapeHits } from '../../sim/shapes';
import type { SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import type { World } from '../../sim/world';
import { monsterStats } from '../balance';
import { area, circle, cone, proj, rect, ring } from '../classes/kit';
import { mLunge, mMelee } from '../monsters/kit';
import {
  KD,
  ONE_SHOT,
  after,
  announce,
  around,
  behindPillar,
  bossScript,
  bskill,
  castAt,
  counterCharge,
  despawn,
  every,
  helpersOf,
  hpScale,
  kb,
  launch,
  lockStagger,
  mech,
  northOf,
  players,
  polarSquare,
  registerEncounter,
  resetStagger,
  setStacks,
  spawnHelper,
  stun,
  tele,
  wipeCast,
  type EncState,
  type EncounterDef,
} from './common';

/**
 * The Sunken Reliquary (Deepvault) — weekly, 2 gates, no revives inside a gate.
 *   Gate I  `drowned_warden`: tide-orb colour soak, stagger check behind a water
 *           shield, tidal pillars (hide from the Deluge).
 *   Gate II `tide_siren`: rotating "whirlpool" pizza floor, in/out donuts, a
 *           personal Siren Song meter (stacking status with thresholds) and
 *           `drowned_thrall` add waves.
 * Both bosses expect to be spawned at the centre of their arena.
 */

const RAD = Math.PI / 180;
const DIAGONALS = [45, 135, 225, 315];

// =============================================================== Gate I · The Drowned Warden

const WARDEN_ATK = 0.9;
const WH = hpScale('raid', WARDEN_ATK);
/** Abyssal Bulwark shield (fraction of max HP) and the stagger bar left once it breaks. */
const BULWARK_SHIELD = 0.03;
const BULWARK_STAGGER = 0.6;

registerStatus({ id: 'tidal_ward', name: 'Abyssal Bulwark', kind: 'buff', icon: 'tidal_ward', description: 'Cannot be staggered while the water shield holds.', stacking: 'refresh', mods: { staggerTakenPct: -1 }, color: 0x3fd0c9 });
registerStatus({ id: 'tidemark_teal', name: 'Tidemark: Teal', kind: 'debuff', icon: 'tidemark_teal', description: 'Intercept a teal tide orb.', stacking: 'refresh', color: 0x2ad0b0 });
registerStatus({ id: 'tidemark_violet', name: 'Tidemark: Violet', kind: 'debuff', icon: 'tidemark_violet', description: 'Intercept a violet tide orb.', stacking: 'refresh', color: 0x9a6aff });
registerStatus({ id: 'tide_blessing', name: 'Tide Blessing', kind: 'buff', icon: 'tide_blessing', description: 'Damage dealt +10%.', stacking: 'refresh', mods: { dmgPct: 0.1 }, color: 0x6ff0d0 });
registerStatus({ id: 'waterlogged', name: 'Waterlogged', kind: 'debuff', icon: 'waterlogged', description: 'Move speed -30%, damage taken +20%.', stacking: 'refresh', mods: { moveSpeedPct: -0.3, dmgTakenPct: 0.2 }, cleansable: true, color: 0x2a5a8a });
registerStatus({ id: 'tidal_surge', name: 'Tidal Surge', kind: 'buff', icon: 'tidal_surge', description: 'Absorbed tide orbs: attack +15% per stack.', stacking: 'stack', maxStacks: 4, mods: { atkPct: 0.15 }, color: 0x2ab0d0 });
registerStatus({ id: 'tide_shelter', name: 'Sheltered', kind: 'buff', icon: 'tide_shelter', description: 'Hidden behind a tidal column: damage taken -90%.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, color: 0xa0f0ff });

const WARDEN_SKILLS: SkillDef[] = [
  bskill({ id: 'warden_trident_sweep', name: 'Trident Sweep', cd: 6, anim: 'Attack', dur: 1.3, actions: [tele(0, cone(6.5, 140), 0.8, 'red', WH(0.18), { reaction: kb(2.5) })] }),
  bskill({ id: 'warden_thrust', name: 'Trident Thrust', cd: 7, anim: 'Attack2', dur: 1.4, actions: [tele(0, rect(11, 2.6), 0.9, 'red', WH(0.24), { reaction: KD })] }),
  bskill({
    id: 'warden_tidal_pillars', name: 'Tidal Pillars', cd: 14, anim: 'Slam', dur: 2.6, element: 'water',
    description: 'Two alternating rings of water columns erupt around the Warden.',
    actions: [
      ...[0, 60, 120, 180, 240, 300].map((a) => tele(0, polarSquare(7, a, 3.6), 1.2, 'red', WH(0.25), { reaction: launch(0.8, 0.9) })),
      ...[30, 90, 150, 210, 270, 330].map((a) => tele(0, polarSquare(13, a, 3.6), 2.0, 'red', WH(0.25), { reaction: launch(0.8, 0.9) })),
    ],
  }),
  bskill({ id: 'warden_undertow', name: 'Undertow', cd: 15, anim: 'Cast', dur: 2.2, element: 'water', actions: [area(0, { shape: circle(12), delay: 0.2, duration: 1.3, telegraph: 'yellow', pull: 3.5, hit: { shape: circle(12), coef: 0 } }), tele(0, circle(5), 1.6, 'red', WH(0.35), { reaction: KD })] }),
  counterCharge({ id: 'warden_riptide_charge', name: 'Riptide Charge', cd: 13, distance: 15, width: 3.4, windup: 1.25, coef: WH(0.3), element: 'water' }),
  bskill({ id: 'warden_anchor_toss', name: 'Anchor Toss', cd: 12, anim: 'Attack2', dur: 2.3, actions: [proj(0.6, { speed: 18, range: 18, radius: 0.8, count: 3, spreadDeg: 40, hit: { shape: circle(0.8), coef: WH(0.15), reaction: KD } }), tele(0, ring(4, 12), 1.8, 'red', WH(0.25), { reaction: kb(3) })] }),
  bskill({ id: 'warden_brine_geyser', name: 'Brine Geyser', cd: 9, anim: 'Cast', dur: 1.6, element: 'water', actions: [tele(0, circle(4), 1.2, 'blue', WH(0.2), { status: [stun(1.2)] }, { anchor: 'point' })] }),
  bskill({ id: 'warden_whirl', name: 'Maelstrom Whirl', cd: 10, anim: 'Attack', dur: 1.5, actions: [tele(0, circle(6), 1.0, 'yellow', WH(0.2), { reaction: kb(5) })] }),
  bskill({ id: 'warden_drown_wave', name: 'Drowning Wave', cd: 16, anim: 'Slam', dur: 2.0, element: 'water', actions: [tele(0, rect(25, 10), 1.4, 'yellow', WH(0.3), { reaction: kb(6) })] }),
  // Mechanic-only.
  bskill({ id: 'warden_riptide_cataclysm', name: 'Riptide Cataclysm', cd: 0, anim: 'Roar', dur: 2.2, element: 'water', description: 'Tide orbs that reached the Warden explode across the vault.', actions: [tele(0, circle(50), 1.5, 'red', WH(0.9), { reaction: kb(5) })] }),
  wipeCast('warden_maelstrom', 'Maelstrom Crush', 16, WH(ONE_SHOT)),
  bskill({ id: 'warden_deluge', name: 'Deluge', cd: 0, anim: 'Roar', dur: 3.8, element: 'water', tags: ['oneshot'], description: 'A wall of water floods the vault. Hide behind a tidal column.', actions: [tele(0, circle(50), 3.0, 'yellow', WH(1.7), { reaction: kb(8) })] }),
];

const tideOrb = (id: string, name: string, tint: number): MonsterDef => ({
  id, name, grade: 'minion', model: 'fx_orb', scale: 1, radius: 0.1, tint,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null, moveSpeed: 1.1,
  params: { helper: true, pickupRadius: 1.3 },
});

const TIDE_PILLAR: MonsterDef = {
  id: 'tide_pillar', name: 'Tidal Column', grade: 'normal', model: 'pillar_ruin', scale: 1.5, radius: 1.3, tint: 0x3a8a9a,
  stats: (l) => monsterStats(l, 'normal', { hp: 50 }), skills: [], brain: 'static', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true },
};

const DROWNED_WARDEN: MonsterDef = {
  id: 'drowned_warden', name: 'The Drowned Warden', title: 'Deepvault — Gate I', creature: 'Undead', grade: 'raid', model: 'boss_warden', scale: 1.15, radius: 2.2, tint: 0x2a9d8f,
  stats: (l) => monsterStats(l, 'raid', { hp: 0.9, atk: WARDEN_ATK }),
  skills: WARDEN_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 6000, loot: 'deepvault_g1',
  weakness: 'lightning', stagger: 800, hpBars: 80,
  params: { arenaRadius: 20, ilvl: 300, gate: 1, material: 'barnacle' },
};

type Colour = 'teal' | 'violet';
interface TideOrbs {
  orbs: { id: number; colour: Colour }[];
  marks: Record<number, Colour>;
  surged: number;
  done: boolean;
}

function startTideOrbs(w: World, b: Actor, e: EncState): void {
  const marks: Record<number, Colour> = {};
  players(w)
    .sort((x, y) => x.id - y.id)
    .forEach((p, i) => {
      const c: Colour = i % 2 === 0 ? 'teal' : 'violet';
      marks[p.id] = c;
      w.grantStatus(p, { id: `tidemark_${c}`, duration: 24 }, b.id);
    });
  const orbs = DIAGONALS.map((a, i) => {
    const colour: Colour = i % 2 === 0 ? 'teal' : 'violet';
    const orb = spawnHelper(w, b, `tide_orb_${colour}`, around(e.arena.center, e.arena.radius * 0.85, a * RAD));
    return { id: orb.id, colour };
  });
  const state: TideOrbs = { orbs, marks, surged: 0, done: false };
  e.data.tide = state;
  after(b, 22, () => endTideOrbs(w, b, state));
}

function absorbOrb(w: World, b: Actor, st: TideOrbs, id: number): void {
  despawn(w, id);
  st.orbs = st.orbs.filter((o) => o.id !== id);
  st.surged++;
  w.grantStatus(b, { id: 'tidal_surge', duration: 90, stacks: 1 }, b.id);
}

function endTideOrbs(w: World, b: Actor, st: TideOrbs): void {
  if (st.done) return;
  st.done = true;
  for (const o of [...st.orbs]) absorbOrb(w, b, st, o.id);
  for (const p of players(w)) w.revokeStatus(p, ['tidemark_teal', 'tidemark_violet'], b.id);
  if (st.surged >= 2) {
    announce(w, 'The Warden drinks the tide — Riptide Cataclysm!', 'warning');
    castAt(b, 'warden_riptide_cataclysm', () => northOf(b));
  } else {
    announce(w, 'The tide orbs are dispersed.', 'success');
  }
}

function watchTideOrbs(w: World, b: Actor, st: TideOrbs): void {
  if (st.done) return;
  for (const o of [...st.orbs]) {
    const orb = w.actors.get(o.id);
    if (!orb?.alive) continue;
    orb.path = [{ ...b.pos }];
    orb.moveGoal = { ...b.pos };
    if (dist(orb.pos, b.pos) <= b.radius + 1) {
      absorbOrb(w, b, st, o.id);
      continue;
    }
    const p = players(w).find((pl) => dist(pl.pos, orb.pos) <= 1.3);
    if (!p) continue;
    despawn(w, o.id);
    st.orbs = st.orbs.filter((x) => x.id !== o.id);
    if (st.marks[p.id] === o.colour) w.grantStatus(p, { id: 'tide_blessing', duration: 15 }, b.id);
    else w.grantStatus(p, { id: 'waterlogged', duration: 10 }, b.id);
  }
  if (st.orbs.length === 0) endTideOrbs(w, b, st);
}

function startBulwark(w: World, b: Actor, e: EncState): void {
  const amount = Math.round(b.stats.maxHp * BULWARK_SHIELD);
  w.grantHeal(b, 0, b.id, amount, 40);
  w.grantStatus(b, { id: 'tidal_ward', duration: 40 }, b.id);
  lockStagger(b);
  e.data.bulwark = true;
  castAt(b, 'warden_maelstrom', () => northOf(b));
}

const WARDEN_SCRIPT = bossScript({
  patterns: [
    { skill: 'warden_trident_sweep', weight: 3, range: [0, 5] },
    { skill: 'warden_thrust', weight: 2.5, range: [0, 9] },
    { skill: 'warden_tidal_pillars', weight: 1.5, range: [0, 14] },
    { skill: 'warden_undertow', weight: 1.2, range: [0, 10] },
    { skill: 'warden_riptide_charge', weight: 2, range: [3, 13] },
    { skill: 'warden_anchor_toss', weight: 1.5, range: [3, 18] },
    { skill: 'warden_brine_geyser', weight: 2, range: [0, 25], target: 'random' },
    { skill: 'warden_whirl', weight: 2, range: [0, 5] },
    { skill: 'warden_drown_wave', weight: 1.5, range: [0, 20], phases: [2] },
  ],
  phaseAt: [50],
  enrageAfter: 480,
  mechanics: [
    mech('Tide Orbs', { pct: 75 }, 'Tide Orbs! Intercept the orbs matching your tidemark before they reach the Warden!', startTideOrbs),
    mech('Abyssal Bulwark', { pct: 55 }, 'Abyssal Bulwark! Break the water shield, then stagger the Warden!', startBulwark),
    mech('Tidal Columns', { pct: 30 }, 'Tidal Columns rise — hide behind one before the Deluge!', (w, b, e) => {
      const ids = DIAGONALS.map((a) => spawnHelper(w, b, 'tide_pillar', around(e.arena.center, e.arena.radius * 0.5, a * RAD)).id);
      e.data.tidePillars = ids;
      castAt(b, 'warden_deluge', () => northOf(b), 6);
    }),
    mech('Abyssal Bulwark II', { pct: 15 }, 'Abyssal Bulwark again! Shatter the shield and stagger!', startBulwark),
  ],
});

const WARDEN_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['tidemark_teal', 'tidemark_violet'],
  watch: (w, b, e) => {
    const tide = e.data.tide as TideOrbs | undefined;
    if (tide) watchTideOrbs(w, b, tide);
    // The shield gates the stagger check: once it breaks, the stagger bar opens.
    if (e.data.bulwark && b.hasStatus('tidal_ward') && b.shield <= 0) {
      w.revokeStatus(b, ['tidal_ward'], b.id);
      resetStagger(b, BULWARK_STAGGER);
      announce(w, 'The bulwark shatters — stagger the Warden now!', 'success');
    }
    // Deluge: players hidden behind a tidal column are sheltered.
    if (e.cur?.skillId === 'warden_deluge' && b.cast) {
      const t = e.time - e.cur.start;
      if (t >= 1.8 && t <= 3.4) {
        const pillars = helpersOf(w, e, 'tide_pillar');
        for (const p of players(w)) {
          if (pillars.some((pl) => behindPillar(b.pos, p.pos, pl.pos, pl.radius))) {
            w.grantStatus(p, { id: 'tide_shelter', duration: 0.5 }, b.id);
            w.grantStatus(p, { id: 'statusImmune', duration: 0.5 }, b.id);
          }
        }
      }
    }
  },
  onCastEnd: {
    warden_maelstrom: (w, b, e, outcome) => {
      e.data.bulwark = false;
      w.revokeStatus(b, ['tidal_ward', 'shield'], b.id);
      if (outcome === 'staggered' || outcome === 'countered') {
        announce(w, 'The Warden is staggered — the Maelstrom collapses!', 'success');
      } else {
        resetStagger(b, 1);
        if (outcome === 'completed') announce(w, 'The Maelstrom crushes the vault…', 'warning');
      }
    },
    warden_deluge: (w, b, e) => {
      for (const id of (e.data.tidePillars as number[] | undefined) ?? []) after(b, 0.5, () => despawn(w, id));
      e.data.tidePillars = [];
    },
  },
};

// =============================================================== Gate II · The Tide Siren

const SIREN_ATK = 0.95;
const SH = hpScale('raid', SIREN_ATK);
/** Song meter: max stacks before the player is enthralled. */
export const SONG_MAX = 6;
/** Resonance threshold (deal and take more damage). */
export const SONG_RESONANCE = 3;
/** Players within this gap of the Siren gain song; farther players lose it. */
export const SONG_RANGE = 10;
/** Seconds between song pulses outside the chorus. */
export const SONG_PULSE = 3;

registerStatus({ id: 'tide_song', name: 'Siren Song', kind: 'debuff', icon: 'tide_song', description: `Personal meter. Near the Siren it rises, away from her it falls. ${SONG_RESONANCE}+: Resonant. ${SONG_MAX}: Enthralled.`, stacking: 'stack', maxStacks: SONG_MAX, color: 0x4fd6c9 });
registerStatus({ id: 'siren_resonance', name: 'Resonant', kind: 'debuff', icon: 'siren_resonance', description: 'Damage dealt +15%, damage taken +20%.', stacking: 'refresh', mods: { dmgPct: 0.15, dmgTakenPct: 0.2 }, color: 0x7ff0e0 });
registerStatus({ id: 'siren_enthralled', name: 'Enthralled', kind: 'debuff', icon: 'siren_enthralled', description: 'Lost in the Siren\'s song: cannot act.', stacking: 'strongest', cc: { disable: true }, color: 0x1d8c8c });
registerStatus({ id: 'siren_crescendo', name: 'Crescendo', kind: 'buff', icon: 'siren_crescendo', description: 'Attack +30%, attack speed +15%.', stacking: 'refresh', mods: { atkPct: 0.3, atkSpeedPct: 0.15 }, color: 0x40e0d0 });

/** Four whirlpool waves; each wave hits everything but one 45° slice, rotating each wave. */
const whirlpool = (dir: 1 | -1) => [0, 1, 2, 3].map((k) => tele(0, cone(22, 315, dir * k * 45 + 180), 1.6 + k * 1.2, 'red', SH(0.4), { reaction: KD }));
const STILL_POOLS = [90, 270].map((a) => polarSquare(9, a, 5));

const SIREN_SKILLS: SkillDef[] = [
  bskill({ id: 'siren_lash', name: 'Tidal Lash', cd: 5, anim: 'Attack', dur: 1.2, element: 'water', actions: [tele(0, cone(7, 90), 0.8, 'red', SH(0.2), { reaction: kb(2) })] }),
  bskill({ id: 'siren_whirlpool_sunwise', name: 'Whirlpool Cadence (sunwise)', cd: 22, anim: 'Cast', dur: 5.8, element: 'water', description: 'The floor spins: four slams light every slice but one, rotating sunwise. Stay on the dark slice.', actions: whirlpool(1) }),
  bskill({ id: 'siren_whirlpool_widdershins', name: 'Whirlpool Cadence (widdershins)', cd: 22, anim: 'Cast', dur: 5.8, element: 'water', description: 'The floor spins: four slams light every slice but one, rotating widdershins. Stay on the dark slice.', actions: whirlpool(-1) }),
  bskill({ id: 'siren_tide_in_out', name: 'Ebb and Flow', cd: 14, anim: 'Cast', dur: 3.0, element: 'water', description: 'Out, then in.', actions: [tele(0, circle(7), 1.4, 'red', SH(0.35), { reaction: KD }), tele(0, ring(7, 22), 2.6, 'red', SH(0.35), { reaction: KD })] }),
  bskill({ id: 'siren_tide_out_in', name: 'Flow and Ebb', cd: 14, anim: 'Cast', dur: 3.0, element: 'water', description: 'In, then out.', actions: [tele(0, ring(7, 22), 1.4, 'red', SH(0.35), { reaction: KD }), tele(0, circle(7), 2.6, 'red', SH(0.35), { reaction: KD })] }),
  bskill({ id: 'siren_echo_bolts', name: 'Echo Bolts', cd: 7, anim: 'Cast', dur: 1.1, element: 'water', actions: [proj(0.6, { speed: 15, range: 22, radius: 0.5, count: 5, spreadDeg: 60, hit: { shape: circle(0.5), coef: SH(0.12), reaction: { kind: 'flinch' } } })] }),
  counterCharge({ id: 'siren_dirge_glide', name: 'Dirge Glide', cd: 13, distance: 12, width: 3, windup: 1.2, coef: SH(0.3), element: 'water' }),
  bskill({ id: 'siren_riptide_orb', name: 'Riptide Orb', cd: 12, anim: 'Cast', dur: 1.4, element: 'water', actions: [proj(0.9, { speed: 6, range: 24, radius: 1, homing: 1.2, hit: { shape: circle(1), coef: 0 }, explode: { shape: circle(3.5), coef: SH(0.3), status: [stun(1.5)] } })] }),
  bskill({ id: 'siren_crashing_wave', name: 'Crashing Wave', cd: 12, anim: 'Attack', dur: 1.9, element: 'water', actions: [tele(0, rect(22, 8), 1.3, 'yellow', SH(0.3), { reaction: kb(6) })] }),
  // Mechanic-only.
  bskill({ id: 'siren_drowning_kiss', name: 'Drowning Kiss', cd: 0, anim: 'Attack', dur: 1.4, element: 'water', actions: [tele(0, circle(3), 1.0, 'red', SH(0.7), { reaction: KD }, { anchor: 'point' })] }),
  bskill({ id: 'siren_chorus', name: 'Chorus of the Drowned', cd: 0, anim: 'Cast', dur: 9.0, element: 'water', description: 'The Siren sings: only the still pools quiet the song.', actions: STILL_POOLS.map((sq) => area(0, { shape: sq, delay: 0.8, duration: 8, telegraph: 'purple', hit: { shape: sq, coef: 0 } })) }),
];

const DROWNED_THRALL: MonsterDef = {
  id: 'drowned_thrall', name: 'Drowned Thrall', grade: 'normal', model: 'mob_ghoul', scale: 1, radius: 0.5, tint: 0x2a9d8f, creature: 'Undead',
  stats: (l) => monsterStats(l, 'normal', { hp: 3, speed: 0.95 }),
  skills: [
    mMelee('thrall_claw', 'Brine Claw', { shape: cone(2.4, 80), coef: 1.2, windup: 0.5, cd: 2.2 }),
    mLunge('thrall_lunge', 'Drowning Lunge', { distance: 4, coef: 1.5, windup: 0.7, cd: 8, range: [2.5, 6] }),
  ],
  brain: 'swarm', aggroRange: 60, leash: 80, xp: 30, loot: null,
};

const TIDE_SIREN: MonsterDef = {
  id: 'tide_siren', name: 'The Tide Siren', title: 'Deepvault — Gate II', creature: 'Undead', grade: 'raid', model: 'boss_matriarch', scale: 1.1, radius: 1.8, tint: 0x1d5c63,
  stats: (l) => monsterStats(l, 'raid', { hp: 1.0, atk: SIREN_ATK }),
  skills: SIREN_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 8000, loot: 'deepvault_g2',
  weakness: 'lightning', stagger: 800, hpBars: 100,
  params: { arenaRadius: 20, ilvl: 300, gate: 2 },
};

function spawnThralls(w: World, b: Actor, e: EncState): void {
  const n = 2 + w.partySize;
  const base = w.rng.range(0, Math.PI * 2);
  for (let i = 0; i < n; i++) spawnHelper(w, b, 'drowned_thrall', around(e.arena.center, e.arena.radius * 0.9, base + (i / n) * Math.PI * 2), { add: true });
}

function enthrall(w: World, b: Actor, p: Actor): void {
  w.grantStatus(p, { id: 'siren_enthralled', duration: 3.5 }, b.id);
  announce(w, `${p.name} is enthralled by the Siren's song!`, 'warning');
  castAt(b, 'siren_drowning_kiss', { ...p.pos });
}

/** One song pulse: updates every player's personal meter (host-authoritative). */
function songPulse(w: World, b: Actor, e: EncState, chorus: boolean, interval: number): void {
  const meter = (e.data.song as Record<number, number> | undefined) ?? {};
  e.data.song = meter;
  const cur = e.cur;
  for (const p of players(w)) {
    const prev = meter[p.id] ?? 0;
    let next: number;
    if (chorus && cur) {
      const still = STILL_POOLS.some((sq) => shapeHits(sq, { origin: cur.origin, facing: cur.aimAngle }, p.pos, 0));
      next = still ? prev - 1 : prev + 1;
    } else {
      next = dist(p.pos, b.pos) - b.radius <= SONG_RANGE ? prev + 1 : prev - 1;
    }
    next = clamp(next, 0, SONG_MAX);
    if (next >= SONG_MAX) {
      enthrall(w, b, p);
      next = 0;
    }
    if (next !== prev) setStacks(w, p, 'tide_song', next, 60, b.id);
    if (next >= SONG_RESONANCE) w.grantStatus(p, { id: 'siren_resonance', duration: interval + 0.5 }, b.id);
    else if (prev >= SONG_RESONANCE) w.revokeStatus(p, ['siren_resonance'], b.id);
    meter[p.id] = next;
  }
}

const SIREN_SCRIPT = bossScript({
  patterns: [
    { skill: 'siren_lash', weight: 3, range: [0, 6] },
    { skill: 'siren_whirlpool_sunwise', weight: 1.2, range: [0, 20] },
    { skill: 'siren_whirlpool_widdershins', weight: 1.2, range: [0, 20], phases: [2, 3] },
    { skill: 'siren_tide_in_out', weight: 1.5, range: [0, 15] },
    { skill: 'siren_tide_out_in', weight: 1.5, range: [0, 15], phases: [2, 3] },
    { skill: 'siren_echo_bolts', weight: 2, range: [3, 22] },
    { skill: 'siren_dirge_glide', weight: 2, range: [3, 11] },
    { skill: 'siren_riptide_orb', weight: 1.5, range: [4, 24] },
    { skill: 'siren_crashing_wave', weight: 1.5, range: [0, 18] },
  ],
  phaseAt: [60, 25],
  enrageAfter: 600,
  mechanics: [
    mech('Call of the Deep', { pct: 80 }, 'The Siren calls the drowned — thralls rise!', spawnThralls),
    mech('Chorus of the Drowned', { pct: 55 }, 'Chorus of the Drowned! Stand in the still pools or be enthralled!', (_w, b) => castAt(b, 'siren_chorus', () => northOf(b))),
    mech('Call of the Deep II', { pct: 40 }, 'More thralls answer the Siren!', spawnThralls),
    mech('Crescendo', { pct: 15 }, 'Crescendo! The Siren sings with all her fury!', (w, b, e) => {
      w.grantStatus(b, { id: 'siren_crescendo', duration: 1e9 }, b.id);
      spawnThralls(w, b, e);
    }),
  ],
});

const SIREN_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['tide_song', 'siren_resonance', 'siren_enthralled'],
  watch: (w, b, e) => {
    const chorus = e.cur?.skillId === 'siren_chorus' && !!b.cast;
    const interval = chorus ? 1.0 : SONG_PULSE;
    if (every(e, chorus ? 'chorus' : 'song', interval)) songPulse(w, b, e, chorus, interval);
  },
};

// =============================================================== exports

export const DEEPVAULT: MonsterDef[] = [
  DROWNED_WARDEN,
  TIDE_SIREN,
  DROWNED_THRALL,
  TIDE_PILLAR,
  tideOrb('tide_orb_teal', 'Teal Tide Orb', 0x2ad0b0),
  tideOrb('tide_orb_violet', 'Violet Tide Orb', 0x9a6aff),
];

export function registerDeepvaultScripts(): void {
  registerEncounter('drowned_warden', WARDEN_ENCOUNTER);
  registerEncounter('tide_siren', SIREN_ENCOUNTER);
  registerBossScript('drowned_warden', WARDEN_SCRIPT);
  registerBossScript('tide_siren', SIREN_SCRIPT);
}
